import axiosInstance from '@api/axios';
import { urls } from '@api/urls';
import { saveBlobToDisk } from '@utils/utils.ts';
import { depositionGenerateOrientation } from '@api/deposition-summary/orientation';

/**
 * Update a deposition summary with new data
 * @param id Deposition summary ID
 * @param data Updated deposition summary data
 * @returns Promise with the updated deposition summary
 */
export const updateDepositionSummary = async (
  id: number | string,
  data: any
) => {
  const response = await axiosInstance.patch(
    urls.depositionSummary.byId(Number(id)),
    data
  );
  return response.data;
};

/**
 * Generate a deposition summary document
 * @param id Deposition summary ID
 * @returns Promise with the generation response
 */
export const generateDepositionSummary = async (id: number) => {
  const response = await axiosInstance.post(
    urls.depositionSummary.generate(id),
    { orientation: depositionGenerateOrientation() }
  );
  return response.data;
};

/**
 * Export a deposition summary as DOCX
 * @param id Deposition summary ID
 * @returns Promise with the document data
 */
export const exportDepositionSummaryDocx = async (
  id: number,
  landscape?: boolean
) => {
  const response = await axiosInstance.get(
    urls.depositionSummary.exportDocx(id),
    {
      responseType: 'blob',
      params: landscape ? { orientation: 'landscape' } : undefined,
    }
  );
  return response.data;
};

/**
 * Download a deposition summary as DOCX and save to disk
 */
export const downloadDepositionSummaryDocx = async (
  id: number,
  landscape?: boolean
): Promise<void> => {
  const response = await axiosInstance.get(
    urls.depositionSummary.exportDocx(id),
    {
      responseType: 'blob',
      params: landscape ? { orientation: 'landscape' } : undefined,
    }
  );
  saveBlobToDisk(response, `deposition_summary_${id}.docx`);
};

/**
 * Delete a deposition summary by ID
 */
export const deleteDepositionSummary = async (id: number): Promise<void> => {
  await axiosInstance.delete(urls.depositionSummary.byId(id));
};

/**
 * Cancel deposition summary generation
 */
export const cancelDepositionSummary = async (id: number): Promise<void> => {
  await axiosInstance.post(urls.depositionSummary.cancelGeneration(id));
};
